'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { useCallback, useEffect } from 'react';
import Flag from 'react-world-flags';
import { ValuteRate } from '../../../lib/all-cbr-rates';
import { getCountryCode } from '../lib/utils';

interface CBRatesTableProps {
  rates: ValuteRate[];
  initialRatesDate?: string | null;
}

const nf2 = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function renderChange(change?: number) {
  if (change === undefined) return <span className="text-gray-400 dark:text-slate-400">N/A</span>;

  const isUp = change > 0;
  const isDown = change < 0;

  return (
    <span
      className={`flex items-center gap-1 ${
        isUp ? 'text-green-500' : isDown ? 'text-red-500' : 'text-gray-400 dark:text-slate-400'
      }`}
    >
      {isUp ? '▲' : isDown ? '▼' : null}
      {nf2.format(Math.abs(change))}
    </span>
  );
}

const CBRateRow = React.memo(function CBRateRow({
  rate,
  isHighlighted,
}: {
  rate: ValuteRate;
  isHighlighted: boolean;
}) {
  const countryCode = getCountryCode(rate.charCode);

  return (
    <tr
      data-code={rate.charCode}
      role="link"
      tabIndex={0}
      aria-label={`Подробнее о валюте ${rate.name}`}
      className={`cursor-pointer transition-colors duration-150 ${
        isHighlighted ? 'bg-slate-700/40' : ''
      } hover:bg-slate-700/70 focus:outline-none focus:ring-2 focus:ring-blue-500`}
    >
      <td className="px-4 py-3 text-sm text-gray-200">
        <div className="flex items-center gap-4">
          <Flag
            code={countryCode}
            height={28}
            width={28}
            className="rounded-sm border border-gray-600 object-cover"
            fallback={<div className="w-6 h-4 bg-gray-600 rounded-sm" />}
          />
          {rate.charCode}
        </div>
      </td>
      <td className="px-4 py-3 text-sm md:text-base text-gray-300">
        <span className={isHighlighted ? 'font-bold text-base md:text-lg' : ''}>
          {rate.nominal !== 1 ? `${rate.nominal} ${rate.name}` : rate.name}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-200">
        <span className={isHighlighted ? 'font-bold text-base' : ''}>
          {nf2.format(rate.value)} ₽
        </span>
      </td>
      <td className="px-4 py-3 text-sm">{renderChange(rate.change)}</td>
    </tr>
  );
});
CBRateRow.displayName = 'CBRateRow';

export function CBRatesTable({ rates, initialRatesDate }: CBRatesTableProps) {
  const router = useRouter();

  const onBodyClick = useCallback(
    (e: React.MouseEvent<HTMLTableSectionElement>) => {
      const tr = (e.target as HTMLElement).closest('tr[data-code]') as HTMLTableRowElement | null;
      const code = tr?.dataset?.code;
      if (code) router.push(`/finance/currency/${code}`);
    },
    [router],
  );

  const onBodyKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTableSectionElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const tr = (e.target as HTMLElement).closest('tr[data-code]') as HTMLTableRowElement | null;
      const code = tr?.dataset?.code;
      if (code) router.push(`/finance/currency/${code}`);
    },
    [router],
  );

  useEffect(() => {
    const fetchMetaAndRefresh = async () => {
      try {
        const res = await fetch('/api/finance/cb-rates/meta', {
          cache: 'no-store',
        });
        if (!res.ok) return;

        const data: { latestDate: string | null } = await res.json();
        if (!data.latestDate || !initialRatesDate) return;

        const initialTs = new Date(initialRatesDate).getTime();
        const latestTs = new Date(data.latestDate).getTime();

        if (Number.isFinite(initialTs) && Number.isFinite(latestTs) && latestTs > initialTs) {
          router.refresh();
        }
      } catch {
        // игнорируем ошибки фонового опроса
      }
    };

    const intervalId = setInterval(fetchMetaAndRefresh, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [initialRatesDate, router]);


  if (!rates || rates.length === 0) {
    return (
      <div className="text-center text-gray-400 dark:text-slate-400 py-10">
        Нет данных о курсах валют.
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Desktop/tablet */}
      <div className="hidden sm:block overflow-x-auto bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-lg shadow-lg">
        <table className="min-w-full divide-y divide-gray-700 dark:divide-slate-700">
          <thead className="bg-gray-700 dark:bg-slate-700">
            <tr className="sticky top-0 z-10 bg-gray-700 dark:bg-slate-700">
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                Код
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                Валюта
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                Курс
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                Изменение
              </th>
            </tr>
          </thead>

          <tbody
            className="divide-y divide-gray-700 dark:divide-slate-700"
            onClick={onBodyClick}
            onKeyDown={onBodyKeyDown}
          >
            {rates.map((rate) => {
              const isHighlighted = rate.charCode === 'USD' || rate.charCode === 'EUR';
              return <CBRateRow key={rate.charCode} rate={rate} isHighlighted={isHighlighted} />;
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="block sm:hidden space-y-2 mt-4">
        {rates.map((rate) => {
          const countryCode = getCountryCode(rate.charCode);
          return (
            <Link
              key={rate.charCode}
              href={`/finance/currency/${rate.charCode}`}
              prefetch={false} // ✅ важно!
              className="flex items-center justify-between p-3 bg-slate-800/70 border border-slate-700 rounded-lg shadow"
            >
              <div className="flex items-center gap-3">
                <Flag
                  code={countryCode}
                  height={20}
                  width={28}
                  className="rounded-sm border border-gray-600 object-cover"
                  fallback={<div className="w-6 h-4 bg-gray-600 rounded-sm" />}
                />
                <div className="flex flex-col">
                  <span className="text-sm text-gray-100 font-semibold">{rate.charCode}</span>
                  <span className="text-xs text-gray-400">
                    {rate.nominal !== 1 ? `${rate.nominal} ${rate.name}` : rate.name}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-100">{nf2.format(rate.value)} ₽</div>
                <div className="text-xs">{renderChange(rate.change)}</div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
